import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { FormikProvider, Form, useFormik } from 'formik';
// mui
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  Stack,
  TextField,
  Grid,
  Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DatePicker from '@mui/lab/DatePicker';
// hooks
import useAuth from 'src/hooks/useAuth';
// redux
import { useDispatch } from 'src/redux/store';
import { addAsset } from 'src/redux/slices/asset';
import { addClientAsset } from 'src/redux/slices/client';
import { deleteFile } from 'src/redux/slices/file';
// utils
import { getNickName } from 'src/utils/formatNickname';
// route
import { PATH_DASHBOARD } from 'src/routes/paths';
// components
import NumberFormat from 'src/components/NumberFormat';
import FileEditor from 'src/components/FileEditor';
import { AddressFields, FormikErrorNotification, TextFieldWithHover } from 'src/components/_dashboard/common';
// constants
import { CO_CLASS, ASSET_STATUS } from 'src/constants/dropdown';
import { fDateZeroTime } from 'src/utils/formatTime';
// ----------------------------------------------------------------------


CollectionDialog.propTypes = {
  handleClose: PropTypes.func.isRequired,
  clientId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  inLoading: PropTypes.bool,
  policies: PropTypes.array.isRequired
};

export default function CollectionDialog({ handleClose, clientId, open, inLoading = false, policies = [] }) {
  const { user } = useAuth();
  const isAdvisor = user && user.role === 'Advisor';
  const [toBeDeleted, setToBeDeleted] = useState([])
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const AddSchema = Yup.object().shape({
    imageFiles: Yup.array(),
    purDate: Yup.date().nullable().min(new Date(1900, 0, 1)),
    inAppDate: Yup.date().nullable().min(new Date(1900, 0, 1)),
    esAppDate: Yup.date().nullable().min(new Date(1900, 0, 1)),
    marketDate: Yup.date().nullable().min(new Date(1900, 0, 1)),
  });

  const formik = useFormik({
    initialValues: {
      itemNumber: '', // Item Number
      class: 'other',
      description: '',
      nickName: '',
      collectionAddressId: '',
      postalCode: '',
      purchasePrice: '',
      purDate: null,
      bankVault: false,
      homeSafe: false,
      consigment: false, // Consigment
      consigAddressId: '',
      loan: false, // Loan
      loanAddressId: '',
      inAppDate: null, // Insurance Appraisal Date
      inAppFirm: '', // Insurance Appraisal Firm
      esAppDate: null, // Estate Appraisal Date
      esAppFirm: '', // Estate Appraisal Firm
      esAppPrice: '', // Estate Appraisal Valuation
      replacementValue: '',
      assetStatus: 'active',
      insured: false,
      insuredValue: '',
      marketValue: '',
      marketValueSource: '',
      marketDate: null,
      rPolicies: [], // Related Policies
      note: '',
      inAppDocs: [], // Insurance Appraisal Documents
      esAppDocs: [], // Estate Appraisal Documents
      conAgrDocs: [], // Consigment Agreement Documents
      lonAgrDocs: [], // Loan Agreement Documents
      imageFiles: [],

    },
    validationSchema: AddSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        handleDeletables();
        const payload = {
          ...values,
          nickName: values.nickName || getNickName(values, 'collection'),
          type: 'collection'
        };
        const res = !!clientId
          ? await dispatch(addClientAsset(payload, clientId, !isAdvisor))
          : await dispatch(addAsset(payload));
        setSubmitting(false);
        if (res && res?.data) {
          enqueueSnackbar('New Asset has been created.', { variant: 'success' });
          const id = res?.data.id || res?.data._id;
          const route = !!clientId
            ? `${PATH_DASHBOARD.root}/asset/collection/${id}/${clientId}`
            : `${PATH_DASHBOARD.root}/asset/collection/${id}`;

          navigate(route);
        } else {
          if (res?.statusCode !== 200 && user.role === 'Advisor') {
            enqueueSnackbar(res?.message || 'You are not permitted to do this action', { variant: 'error' });
          } else {
            enqueueSnackbar('Asset creation error.', { variant: 'error' });
          }
          resetForm();
          handleClose();
        }
      } catch (error) {
        console.error(error);
        setSubmitting(false);
      }
    }
  });

  const { values, errors, touched, resetForm, getFieldProps, setFieldValue, handleSubmit, isSubmitting } = formik;

  const handleDeleteFile = (id, key) => {
    setFieldValue(
      key,
      values[key].filter((doc) => doc.id !== id)
    );
  };

  const handleDeletables = () => {
    toBeDeleted.forEach((id) => dispatch(deleteFile(id)));
    setToBeDeleted([]);
  };


  const handleCancelClick = () => {
    resetForm();
    handleClose();
  };

  return (
    <FormikProvider value={formik}>
      <FormikErrorNotification />
      <Form>
        <Dialog open={open} fullWidth maxWidth="md" onClose={handleClose}>
          <DialogTitle>Add New Asset (Collection)</DialogTitle>
          <Divider />
          <DialogContent>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  {...getFieldProps('itemNumber')}
                  fullWidth
                  type="number"
                  label="Item Number"
                  error={Boolean(touched.itemNumber && errors.itemNumber)}
                  helperText={touched.itemNumber && errors.itemNumber}
                />
                <TextField
                  select
                  fullWidth
                  label="Collection Class"
                  {...getFieldProps('class')}
                  onChange={(e) => setFieldValue('class', e.target.value)}
                >
                  {CO_CLASS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextFieldWithHover
                  {...getFieldProps('nickName')}
                  fullWidth
                  type="string"
                  label="Asset Name"
                  info="Input the asset name"
                />
                <TextField {...getFieldProps('description')} fullWidth type="text" label="Description" />
              </Stack>


              <AddressFields
                isRealEstate={false}
                addId={values.collectionAddressId}
                handleChange={(id) => setFieldValue('collectionAddressId', id)}
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <NumberFormat
                  fullWidth
                  label="Purchase Price"
                  prefix="$"
                  placeholder="$0.00"
                  {...getFieldProps('purchasePrice')}
                  onChange={(e) => setFieldValue('purchasePrice', parseFloat(e.target.values.value))}
                />
                <DatePicker
                  fullWidth
                  label="Purchase Date"
                  {...getFieldProps('purDate')}
                  onChange={(date) => setFieldValue('purDate', fDateZeroTime(date))}
                  inputFormat="MM/dd/yyyy"
                  renderInput={(params) => <TextField fullWidth {...params} />}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <FormControlLabel
                  control={<Checkbox {...getFieldProps('bankVault')} checked={values.bankVault} />}
                  label="Bank Vault"
                  labelPlacement="start"
                />
                <FormControlLabel
                  control={<Checkbox {...getFieldProps('homeSafe')} checked={values.homeSafe} />}
                  label="Home Safe"
                  labelPlacement="start"
                />
              </Stack>
              <Box sx={{ width: '30%' }}>
                <FormControlLabel
                  control={<Switch {...getFieldProps('consigment')} checked={values.consigment} />}
                  label="Consigment"
                  labelPlacement="start"
                />
              </Box>
              {values.consigment && (
                <>
                  <Typography>Consigment Location</Typography>
                  <AddressFields
                    isRealEstate={false}
                    addId={values.consigAddressId}
                    handleChange={(id) => setFieldValue('consigAddressId', id)}
                  />
                </>
              )}
              <Box sx={{ width: '30%' }}>
                <FormControlLabel
                  control={<Switch {...getFieldProps('loan')} checked={values.loan} />}
                  label="Loan"
                  labelPlacement="start"
                />
              </Box>
              {values.loan && (
                <>
                  <Typography>Loan Location</Typography>
                  <AddressFields
                    isRealEstate={false}
                    addId={values.loanAddressId}
                    handleChange={(id) => setFieldValue('loanAddressId', id)}
                  />
                </>
              )}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <NumberFormat
                  fullWidth
                  label="Market Value"
                  prefix="$"
                  placeholder="$0.00"
                  {...getFieldProps('marketValue')}
                  onChange={(e) => setFieldValue('marketValue', parseFloat(e.target.values.value))}
                />

                <NumberFormat
                  fullWidth
                  label={'Insurance Replacement Value'}
                  prefix="$"
                  placeholder="$0.00"
                  {...getFieldProps('replacementValue')}
                  onChange={(e) => setFieldValue('replacementValue', parseFloat(e.target.values.value))}
                />
                <NumberFormat
                  fullWidth
                  label={'Estate Appraisal Valuation'}
                  prefix="$"
                  placeholder="$0.00"
                  {...getFieldProps('esAppPrice')}
                  onChange={(e) => setFieldValue('esAppPrice', parseFloat(e.target.values.value))}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <DatePicker
                  label="Market Valuation Date"
                  value={values.marketDate}
                  onChange={(newDate) => setFieldValue('marketDate', fDateZeroTime(newDate))}
                  inputFormat="MM/dd/yyyy"
                  renderInput={(params) => <TextField fullWidth {...params} />}
                />

                <DatePicker
                  fullWidth
                  label="Insurance Appraisal Date"
                  {...getFieldProps('inAppDate')}
                  onChange={(date) => setFieldValue('inAppDate', fDateZeroTime(date))}
                  inputFormat="MM/dd/yyyy"
                  renderInput={(params) => <TextField fullWidth {...params} />}
                />

                <DatePicker
                  fullWidth
                  label="Estate Appraisal Date"
                  {...getFieldProps('esAppDate')}
                  onChange={(date) => setFieldValue('esAppDate', fDateZeroTime(date))}
                  inputFormat="MM/dd/yyyy"
                  renderInput={(params) => <TextField fullWidth {...params} />}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  {...getFieldProps('marketValueSource')}
                  fullWidth
                  type="text"
                  label="Market Value Source"
                  error={Boolean(touched.marketValueSource && errors.marketValueSource)}
                  helperText={touched.marketValueSource && errors.marketValueSource}
                />

                <TextField {...getFieldProps('inAppFirm')} fullWidth type="text" label="Insurance Appraisal Source" />

                <TextField {...getFieldProps('esAppFirm')} fullWidth type="text" label="Estate Appraisal Source" />
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  select
                  fullWidth
                  label="Asset Status"
                  {...getFieldProps('assetStatus')}
                  onChange={(e) => setFieldValue('assetStatus', e.target.value)}
                  helperText="Please select asset status."
                >
                  {ASSET_STATUS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box sx={{ width: '100%' }}>
                  <FormControlLabel
                    control={<Switch {...getFieldProps('insured')} checked={values.insured} />}
                    label="Insured"
                    labelPlacement="start"
                  />
                </Box>
                {values.insured && (
                  <NumberFormat
                    disabled
                    fullWidth
                    label="Insured Value"
                    prefix="$"
                    placeholder="$0.00"
                    {...getFieldProps('insuredValue')}
                    onChange={(e) => setFieldValue('insuredValue', parseFloat(e.target.values.value))}
                  />
                )}
              </Stack>
              <Autocomplete
                fullWidth
                multiple
                loading={inLoading}
                options={policies}
                ListboxProps={{ sx: { maxHeight: '160px' } }}
                disableCloseOnSelect
                getOptionLabel={(option) => `Collection - ${option.policyNumber} - ${option.nickName}`}
                value={policies.filter((item) => values.rPolicies.some((pol) => pol.id === item.id))}
                onChange={(e, value) =>
                  setFieldValue(
                    'rPolicies',
                    value.map((item) => ({ id: item.id, type: 'collection' }))
                  )
                }
                renderOption={(props, option, { selected }) => {
                  return (
                    <li {...props} key={option.id}>
                      <Checkbox checked={selected} />
                      Collection - {option.policyNumber} - {option.nickName}
                    </li>
                  );
                }}
                renderInput={(params) => <TextField {...params} label="Related Policies" />}
              />
              <Stack spacing={2}>
                <TextField fullWidth multiline minRows={3} maxRows={5} label="Note" {...getFieldProps('note')} />
              </Stack>
            </Stack>
            <Grid item xs={12} md={4}>
              <Box sx={{ pl: 1 }}>
                <Typography variant="h6">Insurance Appraisal Docs</Typography>
                <FileEditor
                  onAddFile={(data) => { values.collection.push(data) }}
                  files={values.collection}
                  deletables={setToBeDeleted}
                  deleteFileFunc={(id) => handleDeleteFile(id, 'inAppDocs')}
                  other={{
                    documentType: 'asset',
                    subType: 'collection',
                    fileType: 'inAppDocs',
                    itemId: '',
                    documentTitle: ''
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ pl: 1 }}>
                <Typography variant="h6">Estate Appraisal Docs</Typography>
                <FileEditor
                  onAddFile={(data) => { values.esAppDocs.push(data) }}
                  files={values.esAppDocs}
                  deletables={setToBeDeleted}
                  deleteFileFunc={(id) => handleDeleteFile(id, 'esAppDocs')}
                  other={{
                    documentType: 'asset',
                    subType: 'collection',
                    fileType: 'esAppDocs',
                    itemId: '',
                    documentTitle: ''
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ pl: 1 }}>
                <Typography variant="h6">Consigment Agreements</Typography>
                <FileEditor
                  onAddFile={(data) => { values.conAgrDocs.push(data) }}
                  files={values.conAgrDocs}
                  deletables={setToBeDeleted}
                  deleteFileFunc={(id) => handleDeleteFile(id, 'conAgrDocs')}
                  other={{
                    documentType: 'asset',
                    subType: 'collection',
                    fileType: 'conAgrDocs',
                    itemId: '',
                    documentTitle: ''
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ pl: 1 }}>
                <Typography variant="h6">Loan Agreements</Typography>
                <FileEditor
                  onAddFile={(data) => { values.lonAgrDocs.push(data) }}
                  files={values.lonAgrDocs}
                  deletables={setToBeDeleted}
                  deleteFileFunc={(id) => handleDeleteFile(id, 'lonAgrDocs')}
                  other={{
                    documentType: 'asset',
                    subType: 'collection',
                    fileType: 'lonAgrDocs',
                    itemId: '',
                    documentTitle: ''
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ pl: 1 }}>
                <Typography variant="h6">Purchase Docs</Typography>
                <FileEditor
                  onAddFile={(data) => { values.purDocs.push(data) }}
                  files={values.purDocs}
                  deletables={setToBeDeleted}
                  deleteFileFunc={(id) => handleDeleteFile(id, 'purDocs')}
                  other={{
                    documentType: 'asset',
                    subType: 'collection',
                    fileType: 'purDocs',
                    itemId: '',
                    documentTitle: ''
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ pl: 1 }}>
                <Typography variant="h6">Asset Images</Typography>
                <FileEditor
                  onAddFile={(data) => { values.imageFiles.push(data) }}
                  files={values.imageFiles}
                  deletables={setToBeDeleted}
                  deleteFileFunc={(id) => handleDeleteFile(id, 'imageFiles')}
                  other={{
                    documentType: 'asset',
                    subType: 'collection',
                    fileType: 'imageFiles',
                    itemId: '',
                    documentTitle: ''
                  }}
                />
              </Box>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelClick} color="inherit">
              Cancel
            </Button>
            <LoadingButton type="submit" variant="contained" loading={isSubmitting} onClick={handleSubmit}>
              Submit
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Form>
    </FormikProvider>
  );
}
